import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, Divider, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

export default function ProductDetails({ product, onClose, onLikeChange, isGiveawayMode, onGiveawaySelect }) {
  const [imgIndex, setImgIndex] = useState(0);
  const [liked, setLiked] = useState(false); // State to track if the item is liked
  const touchStartX = useRef(null);
  const {
    images = [],
    imageUrl,
    brand,
    title,
    price,
    originalPrice,
    productType,
    link,
    discount,
    description,
  } = product;
  // Use images array if available, otherwise fallback to imageUrl
  const imageList = images && images.length > 0 ? images : [imageUrl];

    // Preload all images when component mounts
    useEffect(() => {
      if (imageList && imageList.length > 0) {
        imageList.forEach(imgSrc => {
          const img = new Image();
          img.src = imgSrc;
        });
      }
    }, [imageList]);

  // Check localStorage for liked state on component mount
  useEffect(() => {
    const likedItems = JSON.parse(localStorage.getItem('likedItems')) || [];
    const alreadyLiked = likedItems.some(item => item.title === title);
    if (alreadyLiked) {
      setLiked(true);
    }
  }, [title]);

  if (!product) return null;

  const handlePrev = () => setImgIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
  const handleNext = () => setImgIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));

  const handleLike = (e) => {
    e.stopPropagation();
    const likedItems = JSON.parse(localStorage.getItem('likedItems')) || [];

    if (liked) {
      // Remove from liked by id or title
      const updatedLikedItems = likedItems.filter(
        (item) => item.id !== product.id && item.title !== product.title
      );
      localStorage.setItem('likedItems', JSON.stringify(updatedLikedItems));
      setLiked(false);
    } else {
      // Add to liked if not already present
      const alreadyExists = likedItems.some(
        (item) => item.id === product.id || item.title === product.title
      );
      if (!alreadyExists) {
        likedItems.push(product);
        localStorage.setItem('likedItems', JSON.stringify(likedItems));
      }
      setLiked(true);
    }

    // Notify parent component about the like status change
    if (onLikeChange) {
      onLikeChange();
    }
  };

  // Touch event handlers for swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 40) handlePrev();
    if (dx < -40) handleNext();
    touchStartX.current = null;
  };



  return (
    <Box
      sx={{
        p: 2,
        maxWidth: 500,
        mx: 'auto',
        background: 'white',
        borderRadius: 3,
        boxShadow: '0px 2px 12px rgba(0,0,0,0.08)',
        mt: 3,
        position: 'relative', // Needed for close button positioning
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          background: 'rgba(0,0,0,0.2)',
        }}
        aria-label="Close"
      >
        <CloseIcon />
      </IconButton>

      {/* Image Carousel */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1.2',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
          borderRadius: 2,
          touchAction: 'none',
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          handleTouchStart(e);
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          handleTouchEnd(e);
        }}
        onTouchMove={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {/* Main image */}
        <Box
          component="img"
          src={imageList[imgIndex]}
          alt={title}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 2,
            maxHeight: 340,
          }}
        />
        {/* Preload other images in the background */}
        {imageList.map(
          (img, idx) =>
            idx !== imgIndex && (
              <img
                key={img}
                src={img}
                alt=""
                style={{ display: 'none' }}
                loading="lazy"
              />
            )
        )}
        {imageList.length > 1 && (
          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.7)',
              zIndex: 1,
            }}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        {imageList.length > 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.7)',
              zIndex: 1,
            }}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        )}
        {/* Dots */}
        {imageList.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
            }}
          >
            {imageList.map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: idx === imgIndex ? 'black' : '#bbb',
                  transition: 'background 0.2s',
                }}
              />
            ))}
          </Box>
        )}
        {/* Image count */}
        {imageList.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 16,
              background: 'rgba(0,0,0,0.5)',
              color: 'white',
              borderRadius: 1,
              px: 1,
              fontSize: 12,
              fontWeight: 'bold',
            }}
          >
            {imgIndex + 1} / {imageList.length}
          </Box>
        )}
      </Box>

      {/* Brand and Title */}
      <Typography variant="caption" sx={{ color: 'gray', fontWeight: 'bold', textAlign: 'left' }}>
        {brand}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5, textAlign: 'left' }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'gray', mb: 1, textAlign: 'left' }}>
        {productType}
      </Typography>

      {/* Price and Discount */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'black' }}>
            ${price}
          </Typography>
          {originalPrice && originalPrice !== price && (
            <Typography
              variant="body2"
              sx={{
                textDecoration: 'line-through',
                color: 'gray',
              }}
            >
              ${originalPrice}
            </Typography>
          )}
          {discount && (
            <Typography
              variant="body2"
              sx={{
                color: 'green',
                fontWeight: 'bold',
                ml: 1,
              }}
            >
              {discount}% off
            </Typography>
          )}
        </Box>
        
        {/* Like Button */}
        <IconButton
          onClick={handleLike}
          sx={{
            background: 'rgba(0,0,0,0.04)',
            '&:hover': {
              background: 'rgba(0,0,0,0.08)',
            },
          }}
          aria-label="Like"
        >
          {liked ? (
            <FavoriteIcon sx={{ fontSize: 20, color: 'red' }} />
          ) : (
            <FavoriteBorderIcon sx={{ fontSize: 20, color: 'gray' }} />
          )}
        </IconButton>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Description */}
      {description && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}

      {/* Giveaway Selection Button */}
      {isGiveawayMode && (
        <Button
          variant="contained"
          onClick={onGiveawaySelect}
          startIcon={<CardGiftcardIcon />}
          sx={{
            backgroundColor: '#4caf50',
            color: 'white',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold',
            width: '100%',
            mb: 1,
            '&:hover': {
              backgroundColor: '#45a049',
            },
          }}
        >
          Select This Item for Giveaway
        </Button>
      )}

      {/* External Link */}
      <Button
        variant="contained"
        color="primary"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          backgroundColor: 'black',
          color: 'white',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 'bold',
          width: '100%',
          mt: 1,
        }}
      >
        View on Store
      </Button>
    </Box>
  );
}